/**
 * Family Member Card Component
 * Feature: 004-restore-missing-features
 * Task: T012
 *
 * Displays a family member with edit/delete/setPrimary actions
 */

import { Users, Edit2, Trash2, Star, Phone, Mail, Bell, BellOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { MoreVertical } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { FamilyMember, RelationshipType } from '@/features/shared/types/patient.types'
import { RELATIONSHIP_LABELS } from '@/features/shared/constants/relationships'

interface FamilyMemberCardProps {
  member: FamilyMember
  onEdit?: (member: FamilyMember) => void
  onDelete?: (memberId: string) => void
  onSetPrimary?: (memberId: string) => void
  isLoading?: boolean
  compact?: boolean
}

export function FamilyMemberCard({
  member,
  onEdit,
  onDelete,
  onSetPrimary,
  isLoading = false,
  compact = false,
}: FamilyMemberCardProps) {
  const displayName = member.profiles?.full_name || 'Familiar'
  const email = member.profiles?.email
  const phone = member.whatsapp_number || member.profiles?.phone
  const relationshipLabel =
    RELATIONSHIP_LABELS[member.relationship as RelationshipType] || member.relationship

  if (compact) {
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50">
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-teal-100">
          <Users className="h-5 w-5 text-teal-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 truncate">{displayName}</p>
          <p className="text-sm text-gray-500 truncate">
            {relationshipLabel}
            {phone && ` • ${phone}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {member.is_primary_contact && (
            <Badge variant="secondary" className="bg-teal-100 text-teal-700">
              Primario
            </Badge>
          )}
          {member.accepts_notifications ? (
            <Badge variant="secondary" className="bg-green-100 text-green-700">
              <Bell className="mr-1 h-3 w-3" />
              Notificacoes
            </Badge>
          ) : null}
          {(onEdit || onDelete || onSetPrimary) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onEdit && (
                  <DropdownMenuItem onClick={() => onEdit(member)}>
                    <Edit2 className="mr-2 h-4 w-4" />
                    Editar
                  </DropdownMenuItem>
                )}
                {onSetPrimary && !member.is_primary_contact && (
                  <DropdownMenuItem onClick={() => onSetPrimary(member.id)}>
                    <Star className="mr-2 h-4 w-4" />
                    Tornar primario
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onDelete(member.id)}
                      className="text-red-600 focus:text-red-600"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Remover
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex flex-col p-4 rounded-lg border bg-white transition-colors',
        member.is_primary_contact ? 'border-teal-200 bg-teal-50/50' : 'border-gray-200',
        isLoading && 'opacity-50 pointer-events-none'
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'flex items-center justify-center w-12 h-12 rounded-full',
              member.is_primary_contact ? 'bg-teal-100' : 'bg-gray-100'
            )}
          >
            <Users
              className={cn(
                'h-6 w-6',
                member.is_primary_contact ? 'text-teal-600' : 'text-gray-500'
              )}
            />
          </div>
          <div>
            <p className="font-semibold text-gray-900">{displayName}</p>
            <p className="text-sm text-gray-500">{relationshipLabel}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {member.is_primary_contact && (
            <Badge variant="secondary" className="bg-teal-100 text-teal-700">
              <Star className="mr-1 h-3 w-3" />
              Contato Primario
            </Badge>
          )}
          {member.accepts_notifications ? (
            <Badge variant="secondary" className="bg-green-100 text-green-700">
              <Bell className="mr-1 h-3 w-3" />
              Notificacoes ativas
            </Badge>
          ) : (
            <Badge variant="secondary" className="bg-gray-100 text-gray-500">
              <BellOff className="mr-1 h-3 w-3" />
              Notificacoes desativadas
            </Badge>
          )}
        </div>
      </div>

      {/* Contact Info */}
      <div className="mt-3 space-y-1">
        {phone && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Phone className="h-4 w-4 text-gray-400" />
            <span>{phone}</span>
          </div>
        )}
        {email && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Mail className="h-4 w-4 text-gray-400" />
            <span className="truncate">{email}</span>
          </div>
        )}
        {member.notification_opt_out_source === 'whatsapp' && (
          <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 rounded px-2 py-1 mt-1">
            <BellOff className="h-3 w-3" />
            <span>Desativado pelo familiar via WhatsApp</span>
          </div>
        )}
      </div>

      {/* Actions */}
      {(onEdit || onDelete || onSetPrimary) && (
        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-2">
          {onEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(member)}
              className="flex-1"
            >
              <Edit2 className="mr-2 h-4 w-4" />
              Editar
            </Button>
          )}
          {onSetPrimary && !member.is_primary_contact && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSetPrimary(member.id)}
              className="flex-1"
            >
              <Star className="mr-2 h-4 w-4" />
              Primario
            </Button>
          )}
          {onDelete && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(member.id)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
